import React, { useState, useRef, useEffect, useId } from "react";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { ButtonBase } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { colors } from "../../utils";
import useReducedMotion from "../../hooks/useReducedMotion";

export const StyledTooltip = styled(
  ({ className, color, textColor, width, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  )
)(({ theme, color, textColor, width }) => ({
  zIndex: `1000000 !important`,
  [`& .${tooltipClasses.tooltip}`]: {
    color: textColor || colors.black,
    fontSize: "12px",
    backgroundColor: color || colors.lightBlue,
    borderRadius: "4px",
    padding: "8px",
    width: width || "auto",
    maxWidth: width || 220,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: color || colors.lightBlue,
  },
}));

// D21 two-stage tooltip — dwell before the expanded description reveals.
const DWELL_MS = 1300;
// Finger travel (px) beyond which a touch is a scroll, not a tap.
const TAP_SLOP_PX = 10;
// Segment arc-loader: an SVG <circle> (NOT a path-arc — the Wheel gate
// false-flags path arcs). r=7 -> circumference ~= 2*pi*7 ~= 43.98.
const ARC_R = 7;
const ARC_DASH = 44;

// The staged title content: brand Segment arc-loader (or a static dot under
// reduced-motion) + the short label, then the 1-2 line description at stage 2.
const TwoStageTitle = ({ label, expandedText, stage2, filling, reduced }) => (
  <span
    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
    data-testid="d21-title"
  >
    {!stage2 &&
      (reduced ? (
        <span
          data-testid="d21-dwell-dot"
          aria-hidden="true"
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: colors.PrimaryPurple,
            flex: "0 0 auto",
          }}
        />
      ) : (
        <svg
          data-testid="d21-arc"
          aria-hidden="true"
          width="15"
          height="15"
          viewBox="0 0 20 20"
          fill="none"
          style={{ flex: "0 0 auto", transform: "rotate(-90deg)" }}
        >
          <circle
            cx="10"
            cy="10"
            r={ARC_R}
            fill="none"
            stroke={colors.PrimaryPurple}
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeDasharray={ARC_DASH}
            strokeDashoffset={filling ? 0 : ARC_DASH}
            style={{
              transition: `stroke-dashoffset ${DWELL_MS}ms linear`,
            }}
          />
        </svg>
      ))}
    <span style={{ fontWeight: 600 }}>{label}</span>
    {stage2 && (
      <span data-testid="d21-expanded" style={{ fontWeight: 400 }}>
        {" "}
        {expandedText}
      </span>
    )}
  </span>
);

// A tooltip trigger is often an icon barely 20px across. The click surface must
// still meet the project's 32px minimum WITHOUT changing the layout box the
// surrounding row was designed around — so the extra hit area is painted by a
// centred, transparent ::after overlay rather than by growing the button.
// (Design ruling: click-surface touch target = 32px.)
export const MIN_TOUCH_TARGET = 32;
export const touchTargetSx = {
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: `${MIN_TOUCH_TARGET}px`,
    height: `${MIN_TOUCH_TARGET}px`,
  },
};

// The same 32px rule for a text-label control that is WIDE but short — a table
// sort header (MUI TableSortLabel renders 20px tall) or a text Button in a
// table row. Width is already fine there; only the height needs to grow, so a
// plain min-height is enough (vy-0914-resp-all-table-sort-headers-and-appeal-
// under-32px-768). Used by every sortable settings table.
export const minHeightTouchSx = {
  minHeight: `${MIN_TOUCH_TARGET}px`,
};

/**
 * The same 32px rule for a control that CANNOT carry `sx`.
 *
 * `touchTargetSx` needs emotion, so it reaches MUI components only. A bare
 * `<img role="button">` or a plain DOM node takes an inline style, and an inline
 * style cannot declare a `::after`. This grows the element's own padding box to
 * the 32px floor and cancels the growth again with an equal negative margin, so
 * the hit area meets the ruling while the surrounding row's layout is unchanged
 * — the same layout-neutral promise the `::after` overlay makes, by the only
 * mechanism available without a stylesheet.
 *
 * `content-box` matters: without it, padding would shrink the drawn icon inside
 * a fixed width instead of enlarging the box around it.
 *
 * MEASURED need (web-stg /signup, bundle main.529b92bf.js, 2026-08-05, by hit
 * test rather than by rect): the date-of-birth calendar trigger was 25x24 with
 * no halo, and all four corners of a 32px box around it resolved to something
 * OTHER than the control — 0/4 reachable. The tooltip info icon beside it read
 * 20x20 by rect but 4/4 by hit test, because `touchTargetSx` was already doing
 * this job for it. Rect alone cannot tell those two apart.
 *
 * @param {number} width  the control's own rendered width in px
 * @param {number} height the control's own rendered height in px
 */
export const inlineTouchTargetStyle = (width, height) => {
  const padX = Math.max(0, Math.ceil((MIN_TOUCH_TARGET - width) / 2));
  const padY = Math.max(0, Math.ceil((MIN_TOUCH_TARGET - height) / 2));
  return {
    boxSizing: "content-box",
    padding: `${padY}px ${padX}px`,
    margin: `${-padY}px ${-padX}px`,
  };
};

// The generic name a bare info-icon trigger falls back to. Callers should pass a
// specific `ariaLabel` ("About Sync Clones") whenever they can — this only stops
// a screen reader from announcing a nameless "button".
export const DEFAULT_INFO_LABEL = "More information";

const IyugaTooltip = ({
  tooltip,
  placement = "bottom",
  children,
  width,
  expandedText,
  ariaLabel,
}) => {
  const twoStage = Boolean(expandedText);
  const reduced = useReducedMotion();
  const [open, setOpen] = useState(false);
  const [stage2, setStage2] = useState(false);
  const [filling, setFilling] = useState(false);

  const dwellTimer = useRef(null);
  const rafRef = useRef(null);
  const touchTimer = useRef(null);
  const describeId = useId();

  const startDwell = () => {
    if (!twoStage) return;
    clearTimeout(dwellTimer.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setStage2(false);
    setFilling(false);
    // advance a frame so the dashoffset transition runs from the full offset
    rafRef.current = requestAnimationFrame(() => setFilling(true));
    dwellTimer.current = setTimeout(() => setStage2(true), DWELL_MS);
  };

  const resetDwell = () => {
    clearTimeout(dwellTimer.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setFilling(false);
    setStage2(false);
  };

  const handleTooltipClose = () => {
    setOpen(false);
    resetDwell();
  };

  const handleTooltipOpen = () => {
    setOpen(true);
    startDwell();
  };

  // T22.577: a scroll swipe that merely STARTS on a trigger (e.g. Analytics'
  // "Showing 1 - 10 out of N results" line on a phone) used to open the tooltip
  // on touchstart, leaving the Created/Cloned popper floating over the cards
  // the page scrolls under it. Only a tap -- a touch that ends where it began --
  // opens it now.
  const touchStartPoint = useRef(null);

  const handleTouchStart = (e) => {
    const t = e.touches && e.touches[0];
    touchStartPoint.current = t ? { x: t.clientX, y: t.clientY } : null;
  };

  const handleTouchMove = (e) => {
    const t = e.touches && e.touches[0];
    const start = touchStartPoint.current;
    if (
      start &&
      t &&
      Math.hypot(t.clientX - start.x, t.clientY - start.y) > TAP_SLOP_PX
    ) {
      touchStartPoint.current = null;
    }
  };

  const handleTouchEnd = () => {
    if (!touchStartPoint.current) return;
    touchStartPoint.current = null;
    setOpen(true);
    startDwell();
    // Auto-close on mobile (a little longer when there is more to read).
    clearTimeout(touchTimer.current);
    touchTimer.current = setTimeout(
      () => {
        setOpen(false);
        resetDwell();
      },
      twoStage ? 4000 : 3000
    );
  };

  useEffect(
    () => () => {
      clearTimeout(dwellTimer.current);
      clearTimeout(touchTimer.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    []
  );

  // Screen readers get the FULL text immediately (no timing games) via a
  // visually-hidden node referenced by aria-describedby.
  const fullText = twoStage ? `${tooltip} ${expandedText}` : tooltip;

  // A tooltip only exists in the DOM while it is OPEN, so it can never name its
  // own trigger. Without an explicit label the trigger reaches a screen reader
  // as a bare, nameless "button" — MEASURED on web-stg 2026-07-28, builder
  // Access tab, four such buttons. Prefer the caller's label, then a plain
  // string tooltip, then a generic fallback for the bare-icon trigger.
  const stringTooltip = typeof tooltip === "string" ? tooltip : undefined;
  const childName = ariaLabel || stringTooltip;
  const iconName = childName || DEFAULT_INFO_LABEL;

  const RenderChildren = () => {
    const a11yProps = twoStage ? { "aria-describedby": describeId } : {};
    return children ? (
      <ButtonBase
        type="button"
        aria-label={childName}
        sx={{ alignItems: "flex-start", ...touchTargetSx }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleTooltipOpen}
        {...a11yProps}
      >
        {children}
      </ButtonBase>
    ) : (
      <ButtonBase
        type="button"
        aria-label={iconName}
        sx={touchTargetSx}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleTooltipOpen}
        {...a11yProps}
      >
        <InfoOutlinedIcon
          sx={{ p: 0, m: 0, color: "#667085" }}
          color="primary"
          fontSize="small"
        />
      </ButtonBase>
    );
  };

  const title = twoStage ? (
    <TwoStageTitle
      label={tooltip}
      expandedText={expandedText}
      stage2={stage2}
      filling={filling}
      reduced={reduced}
    />
  ) : (
    tooltip
  );

  // Stage 2 widens to let the 1-2 line description wrap; stage 1 stays tight.
  const effWidth = twoStage && stage2 ? width || 240 : width;

  return (
    <>
      <StyledTooltip
        title={title}
        placement={placement}
        arrow
        width={effWidth}
        open={open}
        onClose={handleTooltipClose}
        onOpen={handleTooltipOpen}
        disableHoverListener={false}
        disableFocusListener={false}
        disableTouchListener={true}
        slotProps={{
          popper: {
            // modifiers: [
            //   {
            //     name: "offset",
            //     options: {
            //       offset: [0, -5],
            //     },
            //   },
            // ],
          },
        }}
      >
        {RenderChildren()}
      </StyledTooltip>
      {twoStage && (
        <span
          id={describeId}
          style={{
            position: "absolute",
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        >
          {fullText}
        </span>
      )}
    </>
  );
};

export default IyugaTooltip;
